import Highcharts from 'highcharts';
import { ReactNode, useMemo } from 'react';
import { useActualColorScheme } from '../../hooks/use-actual-color-scheme.ts';
import { Algorithm, ProsperitySymbol } from '../../models.ts';
import { useStore } from '../../store.ts';
import { getProductChartColor } from '../../utils/colors.ts';
import { Chart } from './Chart.tsx';

/**
 * Official position limits by product (Prosperity 4 + legacy Prosperity 3 logs).
 * When a symbol is missing, `getPositionLimit` falls back to the max |position| in the log.
 */
const POSITION_LIMITS: Record<string, number> = {
  // Prosperity 4 — Tutorial (Round 0)
  TOMATOES: 80,
  EMERALDS: 80,
  // Prosperity 4 — Round 1 (Intara)
  ASH_COATED_OSMIUM: 80,
  INTARIAN_PEPPER_ROOT: 80,
  // Prosperity 3 (optional logs)
  RAINFOREST_RESIN: 50,
  KELP: 50,
  SQUID_INK: 50,
  CROISSANTS: 250,
  JAMS: 350,
  DJEMBES: 60,
  PICNIC_BASKET1: 60,
  PICNIC_BASKET2: 100,
  VOLCANIC_ROCK: 400,
  VOLCANIC_ROCK_VOUCHER_9500: 200,
  VOLCANIC_ROCK_VOUCHER_9750: 200,
  VOLCANIC_ROCK_VOUCHER_10000: 200,
  VOLCANIC_ROCK_VOUCHER_10250: 200,
  VOLCANIC_ROCK_VOUCHER_10500: 200,
  MAGNIFICENT_MACARONS: 75,
};

function getPositionLimit(algorithm: Algorithm, symbol: ProsperitySymbol): number {
  const known = POSITION_LIMITS[symbol];
  if (known !== undefined) {
    return known;
  }

  const positions = algorithm.data.map(row => row.state.position[symbol] || 0);
  if (positions.length === 0) {
    return 1;
  }
  const minPosition = Math.min(...positions);
  const maxPosition = Math.max(...positions);

  return Math.max(Math.abs(minPosition), Math.abs(maxPosition), 1);
}

export interface PositionChartProps {
  symbols: string[];
}

export function PositionChart({ symbols }: PositionChartProps): ReactNode {
  const algorithm = useStore(state => state.algorithm)!;
  const actualColorScheme = useActualColorScheme();

  return useMemo(() => {
    const limits: Record<string, number> = {};
    for (const symbol of symbols) {
      limits[symbol] = getPositionLimit(algorithm, symbol);
    }

    let dataPeak = 0;
    for (const row of algorithm.data) {
      for (const symbol of symbols) {
        dataPeak = Math.max(dataPeak, Math.abs(row.state.position[symbol] || 0));
      }
    }

    let limitPeak = 0;
    for (const symbol of symbols) {
      limitPeak = Math.max(limitPeak, limits[symbol]);
    }

    const half = Math.max(dataPeak, limitPeak, 1);
    const pad = Math.max(2, Math.ceil(half * 0.06));
    const yMax = half + pad;
    const yMin = -yMax;

    const data: Record<string, [number, number][]> = {};
    for (const symbol of symbols) {
      data[symbol] = [];
    }

    for (const row of algorithm.data) {
      for (const symbol of symbols) {
        const position = row.state.position[symbol] || 0;
        data[symbol].push([row.state.timestamp, position]);
      }
    }

    const series: Highcharts.SeriesOptionsType[] = symbols.map((symbol, i) => ({
      type: 'line',
      name: symbol,
      data: data[symbol],
      color: getProductChartColor(symbol, i, actualColorScheme),
      lineWidth: 2.2,
    }));

    return (
      <Chart
        title="Positions"
        series={series}
        min={yMin}
        max={yMax}
        yAxisTitle="Position size (units)"
      />
    );
  }, [actualColorScheme, algorithm, symbols]);
}
